import React, { useState } from 'react';

import { Box, Button, Grid, Paper, Typography, Checkbox, FormControlLabel, makeStyles, Tooltip } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import { FormikProvider, useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import * as yup from 'yup';

import FormDateInput from 'components/FormDateInput';
import FormRichTextInput from 'components/FormRichTextInput';
import FormTextInput from 'components/FormTextInput';
import actionLibraryInstancesApi from 'config/api/actionLibraryInstance/actionLibraryInstance';
import { ActionLibraryInstance, UpdateActionLibraryInstancePayload } from 'config/api/actionLibraryInstance/actionLibraryInstance.types';
import action_library_messages from 'messages/action_library_messages';
import AssetSelectionList from 'pages/ActionLibraryInstance/ActionLibraryInstanceCreatePage/_components/Steps/AssetSelectionList/AssetSelectionList';
import PATHS from 'router/PATHS';

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: theme.spacing(3),
  },
  section: {
    marginBottom: theme.spacing(4),
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(2),
    marginTop: theme.spacing(4),
  },
  stepRow: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  stepContent: {
    flexGrow: 1,
  },
  stepCheckbox: {
    marginLeft: theme.spacing(2),
  },
  infoIcon: {
    cursor: 'help',
    color: theme.palette.action.active,
    fontSize: '1.2rem',
  },
  headerWithInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
}));

const HtmlTooltip = withStyles(theme => ({
  tooltip: {
    backgroundColor: '#f5f5f9',
    color: 'rgba(0, 0, 0, 0.87)',
    maxWidth: 400,
    fontSize: theme.typography.pxToRem(12),
    border: '1px solid #dadde9',
  },
}))(Tooltip);

type Props = {
  instance: ActionLibraryInstance;
};

const ActionLibraryInstanceEditForm: React.FC<Props> = ({ instance }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const styles = useStyles();
  const [loading, setLoading] = useState(false);

  const validationSchema = yup.object({
    instanceName: yup.string().required(t(action_library_messages.instance.validation.instance_name_required)),
    deadlineDate: yup.date().required(t(action_library_messages.instance.validation.deadline_date_required)).nullable(),
    deadlineTime: yup.string().required(t(action_library_messages.instance.validation.deadline_time_required)),
    reminderDate: yup
      .date()
      .required(t(action_library_messages.instance.validation.reminder_date_required))
      .nullable()
      .test('is-before-deadline', t(action_library_messages.instance.validation.reminder_before_deadline), (value, context) => {
        const { deadlineDate, deadlineTime, reminderTime } = context.parent;
        if (!deadlineDate || !deadlineTime || !value || !reminderTime) {
          return true;
        }

        const formatDate = (d: any) => {
          if (d instanceof Date) return d.toISOString().split('T')[0];
          if (typeof d === 'string' && d.includes('T')) return d.split('T')[0];
          return d;
        };

        const dDate = formatDate(deadlineDate);
        const rDate = formatDate(value);
        const deadline = new Date(`${dDate}T${deadlineTime}`);
        const reminder = new Date(`${rDate}T${reminderTime}`);
        return reminder <= deadline;
      }),
    reminderTime: yup.string().required(t(action_library_messages.instance.validation.reminder_time_required)),
    selectedAssets: yup
      .array()
      .min(1, t(action_library_messages.instance.validation.assets_min))
      .required(t(action_library_messages.instance.validation.assets_required)),
  });

  const formik = useFormik({
    initialValues: {
      templateId: '', // We don't have it for an existing instance
      selectedTemplate: null, // We don't have it for an existing instance
      instanceName: instance.title,
      reportedTo: instance.delegatedToFullName || '',
      deadlineDate: instance.deadline ? instance.deadline.split('T')[0] : '',
      deadlineTime: instance.deadline ? instance.deadline.split('T')[1].substring(0, 5) : '16:00',
      reminderDate: instance.reminder ? instance.reminder.split('T')[0] : '',
      reminderTime: instance.reminder ? instance.reminder.split('T')[1].substring(0, 5) : '16:00',
      specialInstruction: instance.specialInstruction || '',
      stepOverrides:
        instance.steps?.map(step => ({
          id: step.id,
          mandatory: step.mandatory,
          title: step.title,
          instruction: step.instruction,
          order: step.order,
        })) || [],
      assetIds: instance.asset.toString(),
      selectedAssets: [
        {
          id: instance.asset,
          name: instance.assetName,
          key: '',
          category: null,
          owner: { id: instance.owner, email: '', fullName: instance.ownerFullName },
        } as any,
      ],
    },
    validationSchema,
    onSubmit: async values => {
      try {
        setLoading(true);

        const payload: UpdateActionLibraryInstancePayload = {
          title: values.instanceName,
          deadline: `${values.deadlineDate}T${values.deadlineTime}:00.000Z`,
          reminder: `${values.reminderDate}T${values.reminderTime}:00.000Z`,
          delegatedTo: instance.delegatedTo, // Keep same for now as we use text input for name
          specialInstruction: values.specialInstruction,
          steps: values.stepOverrides.map(step => ({
            id: step.id,
            mandatory: step.mandatory,
          })),
          assetId: Number(values.selectedAssets[0].id),
        };

        await actionLibraryInstancesApi.updateActionLibraryInstance(instance.id, payload);
        enqueueSnackbar(t(action_library_messages.success.instance_updated), { variant: 'success' });
        history.push(PATHS.ACTION_LIBRARY_INSTANCE_DETAIL.replace(':id', instance.id.toString()));
      } catch (error: any) {
        enqueueSnackbar(t((action_library_messages.errors as any).instance_update_failed || 'Failed to update instance'), {
          variant: 'error',
        });
      } finally {
        setLoading(false);
      }
    },
  });

  const handleMandatoryToggle = (stepId: number) => {
    const newOverrides = formik.values.stepOverrides.map(o => {
      if (o.id === stepId) {
        return { ...o, mandatory: !o.mandatory };
      }
      return o;
    });
    formik.setFieldValue('stepOverrides', newOverrides);
  };

  return (
    <FormikProvider value={formik as any}>
      <form className={styles.root} onSubmit={formik.handleSubmit}>
        <Paper style={{ padding: 24 }}>
          <Box className={styles.section}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormTextInput
                  formik={formik as any}
                  id='instanceName'
                  label={t(action_library_messages.instance.form.instance_name)}
                  required
                />
              </Grid>
              <Grid item md={6} xs={12}>
                <FormDateInput
                  dateTime={false}
                  formik={formik as any}
                  fullWidth
                  id='deadlineDate'
                  label={t(action_library_messages.instance.form.deadline)}
                  required
                />
              </Grid>
              <Grid item md={6} xs={12}>
                <FormDateInput
                  dateTime={false}
                  formik={formik as any}
                  fullWidth
                  id='reminderDate'
                  label={t(action_library_messages.instance.form.reminder)}
                  required
                />
              </Grid>
            </Grid>
          </Box>

          <Box className={styles.section}>
            <FormTextInput formik={formik as any} id='reportedTo' label={t(action_library_messages.instance.form.reported_to)} />
          </Box>

          <Box className={styles.section}>
            <Typography style={{ marginBottom: '15px' }} variant='h6'>
              {t(action_library_messages.instance.form.special_instruction)}
            </Typography>
            <FormRichTextInput formik={formik as any} id='specialInstruction' />
          </Box>

          <Box className={styles.section}>
            <Typography gutterBottom variant='h6'>
              {t(action_library_messages.instance.form.steps_section_title)}
            </Typography>
            <Typography color='textSecondary' paragraph variant='body2'>
              {t(action_library_messages.instance.form.steps_section_description)}
            </Typography>
            <Paper style={{ padding: 16 }} variant='outlined'>
              {formik.values.stepOverrides.map(step => (
                <Box key={step.id} className={styles.stepRow}>
                  <Box className={styles.stepContent}>
                    <Typography variant='subtitle2'>{step.title}</Typography>
                    <Typography color='textSecondary' dangerouslySetInnerHTML={{ __html: step.instruction }} variant='body2' />
                  </Box>
                  <Box className={styles.stepCheckbox}>
                    <HtmlTooltip title={t(action_library_messages.instance.form.mandatory_tooltip)}>
                      <FormControlLabel
                        control={<Checkbox checked={step.mandatory} color='primary' onChange={() => handleMandatoryToggle(step.id)} />}
                        label={t(action_library_messages.instance.form.mandatory)}
                      />
                    </HtmlTooltip>
                  </Box>
                </Box>
              ))}
            </Paper>
          </Box>

          <Box className={styles.section}>
            <Typography gutterBottom variant='h6'>
              {t(action_library_messages.instance.asset_selection.title)}
            </Typography>
            <AssetSelectionList />
          </Box>

          <div className={styles.actions}>
            <Button disabled={loading} onClick={() => history.goBack()}>
              {t(action_library_messages.steps.cancel)}
            </Button>
            <Button color='primary' disabled={loading} type='submit' variant='contained'>
              {t(action_library_messages.success.save_changes)}
            </Button>
          </div>
        </Paper>
      </form>
    </FormikProvider>
  );
};

export default ActionLibraryInstanceEditForm;
